// src/utils/optimizedApiClient.js
import apiClient from '../api/client';
import apiCache from './apiCache';

/**
 * Optimized API client wrapper with intelligent caching and request deduplication
 */

// Map to store pending requests to prevent duplicate API calls
const pendingRequests = new Map();

// Configuration for different endpoint caching strategies
const CACHE_CONFIG = {
  // Subscription data - cache for 2 minutes as it can change during upgrades/downgrades
  '/billing/subscription': { ttl: 2 * 60 * 1000, cacheable: true },
  
  // Subscription plans - cache for 30 minutes as they rarely change
  '/billing/subscription-plans': { ttl: 30 * 60 * 1000, cacheable: true },
  '/billing/plans': { ttl: 30 * 60 * 1000, cacheable: true },
  
  // User profile - cache for 5 minutes
  '/auth/me': { ttl: 5 * 60 * 1000, cacheable: true },
  
  // Settings and configuration - cache for 10 minutes
  '/settings': { ttl: 10 * 60 * 1000, cacheable: true },
  
  // Don't cache authentication endpoints
  '/auth/logout': { cacheable: false },
  '/auth/refresh-token': { cacheable: false },
  
  // Don't cache mutation endpoints
  '/billing/upgrade-subscription': { cacheable: false },
  '/billing/downgrade-subscription': { cacheable: false },
  '/billing/cancel-subscription': { cacheable: false },
  '/billing/reactivate-subscription': { cacheable: false },
};

/**
 * Get cache configuration for an endpoint
 */
function getCacheConfig(url) {
  // Find exact match first
  if (CACHE_CONFIG[url]) {
    return CACHE_CONFIG[url];
  }
  
  // Find pattern match
  for (const pattern in CACHE_CONFIG) {
    if (url.includes(pattern)) {
      return CACHE_CONFIG[pattern];
    }
  }
  
  // Default: don't cache POST/PUT/DELETE requests, cache GET requests for 1 minute
  return {
    cacheable: true,
    ttl: 60 * 1000 // 1 minute default
  };
}

/**
 * Optimized GET request with caching and deduplication
 */
async function optimizedGet(url, config = {}) {
  const cacheConfig = getCacheConfig(url);
  const cacheKey = apiCache.generateKey(url, config.params);
  
  // Check cache first if cacheable
  if (cacheConfig.cacheable) {
    const cachedData = apiCache.get(cacheKey);
    if (cachedData) {
      console.log(`🎯 Cache hit for ${url}`);
      return Promise.resolve({ data: cachedData });
    }
  }
  
  // Check if request is already pending
  if (pendingRequests.has(cacheKey)) {
    console.log(`⏳ Deduplicating request for ${url}`);
    return pendingRequests.get(cacheKey);
  }
  
  // Make the request
  const requestPromise = apiClient.get(url, config)
    .then(response => {
      // Cache successful responses if cacheable
      if (cacheConfig.cacheable && response.status === 200) {
        apiCache.set(cacheKey, response.data, cacheConfig.ttl);
      }
      return response;
    })
    .finally(() => {
      // Remove from pending requests
      pendingRequests.delete(cacheKey);
    });
  
  // Store pending request
  pendingRequests.set(cacheKey, requestPromise);
  
  return requestPromise;
}

/**
 * POST request that invalidates related cache entries
 */
async function optimizedPost(url, data, config = {}) {
  const response = await apiClient.post(url, data, config);
  
  // Invalidate related cache entries after successful mutations
  if (response.status >= 200 && response.status < 300) {
    if (url.includes('/billing/')) {
      // Clear billing-related cache entries
      apiCache.delete('/billing/subscription');
      apiCache.delete('/billing/plans');
      apiCache.delete('/billing/subscription-plans');
      apiCache.delete('/auth/me'); // User profile may have updated subscription info
    }
    
    if (url.includes('/auth/')) {
      // Clear auth-related cache entries
      apiCache.delete('/auth/me');
    }
  }
  
  return response;
}

/**
 * PUT request that invalidates related cache entries
 */
async function optimizedPut(url, data, config = {}) {
  const response = await apiClient.put(url, data, config);
  
  // Invalidate related cache entries after successful mutations
  if (response.status >= 200 && response.status < 300) {
    const cacheKey = apiCache.generateKey(url);
    apiCache.delete(cacheKey);
    
    // Invalidate related entries
    if (url.includes('/billing/')) {
      apiCache.delete('/billing/subscription');
      apiCache.delete('/auth/me');
    }
  }
  
  return response;
}

/**
 * DELETE request that invalidates related cache entries
 */
async function optimizedDelete(url, config = {}) {
  const response = await apiClient.delete(url, config);
  
  // Invalidate related cache entries after successful deletions
  if (response.status >= 200 && response.status < 300) {
    const cacheKey = apiCache.generateKey(url);
    apiCache.delete(cacheKey);
  }
  
  return response;
}

/**
 * Preload commonly accessed endpoints
 */
async function preloadEssentialData() {
  try {
    // Skip preload on admin routes
    const isAdminRoute = window.location.pathname.startsWith('/admin');
    if (isAdminRoute) {
      console.log('🔒 Skipping preload - admin route detected');
      return;
    }

    // Check if user has auth token before preloading subscription data
    const authToken = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
    if (!authToken) {
      console.log('🔒 Skipping preload - user not authenticated');
      return;
    }
    
    console.log('🚀 Preloading essential subscription data...');
    
    // Preload subscription plans (these rarely change and don't require auth)
    optimizedGet('/billing/subscription-plans').catch(err => {
      console.log('Preload failed for subscription plans:', err.message);
    });
    
    // Preload current subscription status (requires auth)
    optimizedGet('/billing/subscription').catch(err => {
      console.log('Preload failed for subscription:', err.message);
    });
    
  } catch (error) {
    console.log('Preload error:', error);
  }
}

/**
 * Manual cache invalidation for specific patterns
 */
function invalidateCache(pattern) {
  const keys = [...apiCache.cache.keys()];
  keys.forEach(key => {
    if (key.includes(pattern)) {
      apiCache.delete(key);
    }
  });
}

/**
 * Get cache statistics for debugging
 */
function getCacheStats() {
  return {
    ...apiCache.getStats(),
    pendingRequests: pendingRequests.size,
    config: CACHE_CONFIG
  };
}

// Export the optimized client
const optimizedApiClient = {
  get: optimizedGet,
  post: optimizedPost,
  put: optimizedPut,
  delete: optimizedDelete,
  preloadEssentialData,
  invalidateCache,
  getCacheStats,
  // Direct access to original client for non-cacheable operations
  raw: apiClient
};

// Preload essential data on client initialization
if (typeof window !== 'undefined') {
  // Only run in browser environment
  setTimeout(() => {
    preloadEssentialData();
  }, 1000); // Delay to allow auth token to be set
}

export default optimizedApiClient;